/**
 * Shared chrome for the readiness dashboards (demo placeholders).
 *
 * Both the Care Readiness (pre-admit) and Discharge Readiness (pre-discharge)
 * dashboards render the same static card grid: a dark header bar, a two-column
 * grid of coordination tasks driven by our AI agents (with occasional human or
 * vendor overrides), and a footer summary. Only the task list and the header /
 * footer copy differ, so those are supplied by the calling dashboard.
 */

import { Box, Button, Chip, Grid2 as Grid, Paper, Typography } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckIcon from "@mui/icons-material/Check";
import ComputerIcon from "@mui/icons-material/Computer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SmsIcon from "@mui/icons-material/Sms";

// Placeholder palette pulled from the design mockup (v2). Kept local since this
// is a demo-only component with no themed counterpart yet.
export const COLORS = {
  pageBg: "#fdf2ec",
  headerBg: "#241b3a",
  headerText: "#ffffff",
  cardBg: "#ffffff",
  iconSquareBg: "#241b3a",
  statusCompleteBg: "#e2574c",
  statusIncompleteRing: "#e3a53c",
  agentBadge: "#26315c",
  humanBadge: "#b98a2e",
  vendorBadge: "#3f7d3a",
  declinedRed: "#c0392b",
  footerBg: "#e2574c",
  additionalButtonBg: "#241b3a",
} as const;

export type Channel = { label: string; icon: SvgIconComponent; color: string };

export const AI_CALL: Channel = { label: "AI Agent · Call", icon: PhoneIcon, color: COLORS.agentBadge };
export const AI_SMS: Channel = { label: "AI Agent · SMS", icon: SmsIcon, color: COLORS.agentBadge };
export const AI_PORTAL: Channel = { label: "AI Agent · Portal", icon: ComputerIcon, color: COLORS.agentBadge };
export const HUMAN_OVERRIDE: Channel = { label: "Human Override", icon: PersonIcon, color: COLORS.humanBadge };
export const VENDOR_DISPATCH: Channel = { label: "Vendor Dispatch", icon: LocalShippingIcon, color: COLORS.vendorBadge };

export type CoordinationTask = {
  icon: SvgIconComponent;
  title: string;
  subtitle: string;
  channel: Channel;
  complete: boolean;
  timing: string;
  declined?: string;
};

const ChannelBadge = ({ channel }: { channel: Channel }) => {
  const Icon = channel.icon;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16, color: `${COLORS.headerText} !important` }} />}
      label={channel.label}
      sx={{
        bgcolor: channel.color,
        color: COLORS.headerText,
        fontWeight: 600,
        fontSize: "0.7rem",
        height: 24,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const DeclinedNote = ({ declined }: { declined?: string }) => {
  if (declined) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
        <OutlinedFlagIcon sx={{ fontSize: 14, color: COLORS.declinedRed }} />
        <Typography variant="caption" sx={{ color: COLORS.declinedRed, fontWeight: 600 }}>
          Declined: {declined}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
      <CheckIcon sx={{ fontSize: 14, color: "text.secondary" }} />
      <Typography variant="caption" color="text.secondary">
        No items declined
      </Typography>
    </Box>
  );
};

const TaskCard = ({ task }: { task: CoordinationTask }) => {
  const Icon = task.icon;
  return (
    <Paper
      elevation={1}
      sx={{
        position: "relative",
        bgcolor: COLORS.cardBg,
        borderRadius: 2,
        p: 1.75,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: COLORS.iconSquareBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ color: COLORS.headerText, fontSize: 22 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {task.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {task.subtitle}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <ChannelBadge channel={task.channel} />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {task.timing}
          </Typography>
        </Box>
        <DeclinedNote declined={task.declined} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 20,
          height: 20,
          borderRadius: "50%",
          bgcolor: task.complete ? COLORS.statusCompleteBg : "transparent",
          border: task.complete ? "none" : `2px solid ${COLORS.statusIncompleteRing}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {task.complete && <CheckIcon sx={{ color: COLORS.headerText, fontSize: 14 }} />}
      </Box>
    </Paper>
  );
};

export type ReadinessDashboardShellProps = {
  /** Left-hand header text, e.g. "Hospital at Home · Discharge Readiness Dashboard". */
  title: string;
  /** Right-hand header text, e.g. "Patient: W. Nakamura · Discharge Target: Today, 4:00 PM". */
  meta: string;
  tasks: CoordinationTask[];
  /** Footer sentence; receives the completed/total counts already computed. */
  footerText: (completeCount: number, total: number) => string;
};

export const ReadinessDashboardShell = ({ title, meta, tasks, footerText }: ReadinessDashboardShellProps) => {
  const completeCount = tasks.filter((t) => t.complete).length;

  return (
    <Box sx={{ bgcolor: COLORS.pageBg }}>
      {/* Header bar */}
      <Box
        sx={{
          bgcolor: COLORS.headerBg,
          color: COLORS.headerText,
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {meta}
        </Typography>
      </Box>

      {/* Task grid */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {tasks.map((task) => (
            <Grid key={task.title} size={{ xs: 12, md: 6 }}>
              <TaskCard task={task} />
            </Grid>
          ))}
        </Grid>

        {/* Footer summary + action */}
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
          <Box
            sx={{
              flex: 1,
              minWidth: 260,
              bgcolor: COLORS.footerBg,
              color: COLORS.headerText,
              borderRadius: 1,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CheckIcon sx={{ fontSize: 22 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {footerText(completeCount, tasks.length)}
            </Typography>
          </Box>
          <Button
            disabled
            startIcon={<AddCircleIcon />}
            sx={{
              bgcolor: COLORS.additionalButtonBg,
              color: COLORS.headerText,
              fontWeight: 700,
              px: 2.5,
              "&.Mui-disabled": { bgcolor: COLORS.additionalButtonBg, color: COLORS.headerText, opacity: 0.95 },
            }}
          >
            Additional Coordination
          </Button>
        </Box>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: 3, mt: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CheckIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              No items declined by patient/family
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <OutlinedFlagIcon sx={{ fontSize: 14, color: COLORS.declinedRed }} />
            <Typography variant="caption" color="text.secondary">
              One or more items declined — see note
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReadinessDashboardShell;
